/**
 * Order Book and Market Depth Service
 * 
 * This service handles:
 * 1. Real-time order book data from Binance WebSocket
 * 2. Market depth visualization data
 * 3. Order book updates and management
 */

export interface OrderBookEntry {
  price: number;
  quantity: number;
  total: number;
}

export interface OrderBookData {
  symbol: string;
  lastUpdateId: number;
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  spread: number;
  spreadPercentage: number;
  timestamp: number;
}

export interface MarketDepthData {
  symbol: string;
  sells: OrderBookEntry[];
  buys: OrderBookEntry[];
  maxVolume: number;
  timestamp: number;
}

export class OrderBookService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private isDisconnecting = false;
  private orderBookData = new Map<string, OrderBookData>();
  private marketDepthData = new Map<string, MarketDepthData>();
  private listeners: Set<(symbol: string, data: OrderBookData) => void> = new Set();
  private depthListeners: Set<(symbol: string, data: MarketDepthData) => void> = new Set();
  private subscribedSymbols = new Set<string>();
  private currentSymbol: string | null = null;
  private isAuthenticated = false;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private disconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pendingConnectTimer: NodeJS.Timeout | null = null;
  private pendingDisconnectTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Don't auto-connect - wait for symbol subscription
  }

  /**
   * Start the service when user is authenticated
   */
  public startService() {
    if (this.isAuthenticated) {
      return;
    }
    
    this.isAuthenticated = true;
  }

  /**
   * Stop the service when user logs out
   */
  public stopService() {
    if (!this.isAuthenticated) {
      return;
    }
    
    this.isAuthenticated = false;
    // Cancel queued work before closing, so nothing can reopen the socket after logout.
    this.cancelPendingDisconnect();
    if (this.pendingConnectTimer) {
      clearTimeout(this.pendingConnectTimer);
      this.pendingConnectTimer = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.gracefulDisconnect();
  }

  /**
   * Check if service is authenticated and running
   */
  public isServiceRunning(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Subscribe to order book updates for a specific symbol
   */
  subscribeToOrderBook(symbol: string, callback: (symbol: string, data: OrderBookData) => void): () => void {
    if (!this.isAuthenticated) {
      console.warn('OrderBookService: User must be authenticated to subscribe to order book data');
      // Provide fallback data without connecting
      const fallbackData = this.createFallbackData(symbol);
      callback(symbol, fallbackData);
      
      // Return a no-op unsubscribe function
      return () => {};
    }

    // A subscriber has arrived, so any teardown queued by the previous unmount is void.
    this.cancelPendingDisconnect();

    // If symbol has changed, gracefully disconnect old WebSocket and connect to new symbol
    if (this.currentSymbol !== symbol) {
      // Force disconnect immediately to prevent race conditions
      this.forceDisconnect();
      this.currentSymbol = symbol;
      this.subscribedSymbols.clear();
      this.subscribedSymbols.add(symbol);
      
      // Add a small delay to ensure the previous connection is fully closed
      this.scheduleConnect(symbol);
      
      // Don't provide fallback data immediately - let the loading state show
      // The data will be provided when the WebSocket connects
    } else if (!this.subscribedSymbols.has(symbol)) {
      this.subscribedSymbols.add(symbol);
      this.connectWebSocket(symbol);
      
      // Provide fallback data immediately while connecting
      const fallbackData = this.createFallbackData(symbol);
      this.orderBookData.set(symbol, fallbackData);
      callback(symbol, fallbackData);
    }

    this.listeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
      
      // The symbol stays subscribed until both panels have gone, otherwise the surviving
      // panel would lose its reconnect eligibility on the shared socket.
      if (this.listeners.size === 0) {
        this.scheduleDisconnectIfIdle();
      }
    };
  }

  /**
   * Subscribe to market depth updates for a specific symbol
   */
  subscribeToMarketDepth(symbol: string, callback: (symbol: string, data: MarketDepthData) => void): () => void {
    if (!this.isAuthenticated) {
      console.warn('OrderBookService: User must be authenticated to subscribe to market depth data');
      // Provide fallback data without connecting
      const fallbackDepthData = this.createFallbackDepthData(symbol);
      callback(symbol, fallbackDepthData);
      
      // Return a no-op unsubscribe function
      return () => {};
    }

    // A subscriber has arrived, so any teardown queued by the previous unmount is void.
    this.cancelPendingDisconnect();

    // If symbol has changed, gracefully disconnect old WebSocket and connect to new symbol
    if (this.currentSymbol !== symbol) {
      // Force disconnect immediately to prevent race conditions
      this.forceDisconnect();
      this.currentSymbol = symbol;
      this.subscribedSymbols.clear();
      this.subscribedSymbols.add(symbol);
      
      // Add a small delay to ensure the previous connection is fully closed
      this.scheduleConnect(symbol);
      
      // Don't provide fallback data immediately - let the loading state show
      // The data will be provided when the WebSocket connects
    } else if (!this.subscribedSymbols.has(symbol)) {
      this.subscribedSymbols.add(symbol);
      this.connectWebSocket(symbol);
      
      // Provide fallback data immediately while connecting
      const fallbackDepthData = this.createFallbackDepthData(symbol);
      this.marketDepthData.set(symbol, fallbackDepthData);
      callback(symbol, fallbackDepthData);
    }

    this.depthListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.depthListeners.delete(callback);
      
      // As above: teardown is decided by both listener sets, not this one alone.
      if (this.depthListeners.size === 0) {
        this.scheduleDisconnectIfIdle();
      }
    };
  }

  /**
   * The order book and the market depth panels are separate subscribers sharing a single
   * WebSocket, and both are unmounted together when the right panel is closed. Teardown is
   * therefore deferred and cancellable: closing the panel schedules a disconnect, and
   * reopening it cancels that disconnect instead of letting it close the socket that the
   * remounted panels have just reopened. Previously both timers were untracked, so a quick
   * close/open left the panels connected to a socket that was about to be killed — which
   * looked like the panel hanging and then reloading itself.
   */
  private cancelPendingDisconnect() {
    if (this.pendingDisconnectTimer) {
      clearTimeout(this.pendingDisconnectTimer);
      this.pendingDisconnectTimer = null;
    }
  }

  private scheduleConnect(symbol: string) {
    if (this.pendingConnectTimer) clearTimeout(this.pendingConnectTimer);
    this.pendingConnectTimer = setTimeout(() => {
      this.pendingConnectTimer = null;
      this.connectWebSocket(symbol);
    }, 100);
  }

  private scheduleDisconnectIfIdle() {
    this.cancelPendingDisconnect();
    this.pendingDisconnectTimer = setTimeout(() => {
      this.pendingDisconnectTimer = null;
      // Only tear down once neither panel is listening; the socket is shared.
      if (this.listeners.size === 0 && this.depthListeners.size === 0) {
        if (this.currentSymbol) this.subscribedSymbols.delete(this.currentSymbol);
        this.gracefulDisconnect();
        this.currentSymbol = null;
      }
    }, 100);
  }

  /**
   * Create fallback order book data for a symbol
   */
  private createFallbackData(symbol: string): OrderBookData {
    // Return empty data with 0.00 values instead of realistic dummy data
    const bids: OrderBookEntry[] = [];
    const asks: OrderBookEntry[] = [];
    
    // Generate 50 bid levels with 0.00 values (instead of 4)
    for (let i = 0; i < 100; i++) {
      bids.push({
        price: 0.00,
        quantity: 0.00,
        total: 0.00
      });
    }
    
    // Generate 50 ask levels with 0.00 values (instead of 4)
    for (let i = 0; i < 100; i++) {
      asks.push({
        price: 0.00,
        quantity: 0.00,
        total: 0.00
      });
    }
    
    return {
      symbol,
      lastUpdateId: Date.now(),
      bids,
      asks,
      spread: 0.00,
      spreadPercentage: 0.00,
      timestamp: Date.now()
    };
  }

  /**
   * Create fallback market depth data for a symbol
   */
  private createFallbackDepthData(symbol: string): MarketDepthData {
    const sells: OrderBookEntry[] = [];
    const buys: OrderBookEntry[] = [];
    
    // Generate 1 sell level with 0.00 values (instead of 4)
    for (let i = 0; i < 1; i++) {
      sells.push({
        price: 0.00,
        quantity: 0.00,
        total: 0.00
      });
    }
    
    // Generate 1 buy level with 0.00 values (instead of 4)
    for (let i = 0; i < 1; i++) {
      buys.push({
        price: 0.00,
        quantity: 0.00,
        total: 0.00
      });
    }
    
    return {
      symbol,
      sells,
      buys,
      maxVolume: 0.00,
      timestamp: Date.now()
    };
  }



  /**
   * Gracefully disconnect WebSocket with proper cleanup
   */
  private gracefulDisconnect(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.ws || this.isDisconnecting) {
        resolve();
        return;
      }

      this.isDisconnecting = true;

      // Clear any existing timeouts
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      if (this.disconnectTimeout) {
        clearTimeout(this.disconnectTimeout);
        this.disconnectTimeout = null;
      }

      // Set a timeout to force resolve if WebSocket doesn't close properly
      this.disconnectTimeout = setTimeout(() => {
        this.forceDisconnect();
        this.isDisconnecting = false;
        resolve();
      }, 2000);

      // Handle WebSocket close event
      const onClose = () => {
        this.isDisconnecting = false;
        if (this.disconnectTimeout) {
          clearTimeout(this.disconnectTimeout);
          this.disconnectTimeout = null;
        }
        resolve();
      };

      if (this.ws.readyState === WebSocket.OPEN) {
        // Set up close handler before closing
        this.ws.onclose = onClose;
        this.ws.close(1000, 'Normal closure'); // Use proper close code
      } else if (this.ws.readyState === WebSocket.CONNECTING) {
        // If still connecting, wait a bit then force close
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.onclose = onClose;
            this.ws.close(1000, 'Normal closure');
          } else {
            onClose();
          }
        }, 500);
      } else {
        // Already closed or closing
        onClose();
      }
    });
  }

  /**
   * Force disconnect WebSocket without waiting
   */
  private forceDisconnect() {
    if (this.ws) {
      // Remove all event listeners first
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;
      
      // Close the connection with proper close code
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        try {
          this.ws.close(1000, 'Normal closure');
        } catch (error) {
          // Ignore close errors
        }
      }
      
      // Clear the reference
      this.ws = null;
    }
    
    // Clear any pending timeouts
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout);
      this.disconnectTimeout = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.pendingConnectTimer) {
      clearTimeout(this.pendingConnectTimer);
      this.pendingConnectTimer = null;
    }
    
    // Reset state flags
    this.isConnecting = false;
    this.isDisconnecting = false;
  }

  /**
   * Connect to Binance WebSocket for order book data
   */
  private connectWebSocket(symbol: string) {
    if (!this.isAuthenticated || this.isConnecting || this.isDisconnecting) return;
    
    // If WebSocket is already open for the same symbol, don't create a new one
    if (this.ws?.readyState === WebSocket.OPEN && this.currentSymbol === symbol) {
      return;
    }
    
    // If we're already connecting to the same symbol, don't create another connection
    if (this.isConnecting && this.currentSymbol === symbol) {
      return;
    }
    
    // Clear any existing connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    this.isConnecting = true;
    
    // Use the order book stream for the specific symbol
    // Format: <symbol>@depth<levels>@<speed>
    const streamName = `${symbol.toLowerCase()}@depth20@100ms`;
    this.ws = new WebSocket(`wss://stream.binance.com:9443/ws/${streamName}`);
    
    // Set connection timeout
    this.connectionTimeout = setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        console.warn(`WebSocket connection timeout for ${symbol}`);
        this.ws.close(1000, 'Connection timeout');
      }
    }, 10000); // 10 second timeout
    
    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      // Reset the backoff too, otherwise it stayed inflated for the rest of the session.
      this.reconnectDelay = 1000;
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      this.isConnecting = false;
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      // Only attempt reconnect if it wasn't a normal closure
      if (event.code !== 1000 && this.subscribedSymbols.has(symbol)) {
        this.handleReconnect(symbol);
      }
    };

    this.ws.onerror = (error) => {
      this.isConnecting = false;
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      // Only log errors that aren't related to normal closure
      if (this.ws?.readyState !== WebSocket.CLOSING) {
        console.error(`Order Book WebSocket error for ${symbol}:`, error);
      }
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(message: any) {
    try {
      // Binance order book stream sends data directly, not wrapped in a stream object
      if (message.lastUpdateId && (message.bids || message.asks)) {
        // Extract symbol from the WebSocket URL or use a fallback
        const symbol = this.getCurrentSymbol();
        if (symbol) {
          this.processOrderBookData(symbol, message);
        }
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  }

  /**
   * Get the current symbol being tracked
   */
  private getCurrentSymbol(): string | null {
    return Array.from(this.subscribedSymbols)[0] || null;
  }

  /**
   * Process order book data from WebSocket
   */
  private processOrderBookData(symbol: string, data: any) {
    try {
      const lastUpdateId = data.lastUpdateId || Date.now();
      
      // Process bids (buy orders) - limit processing for performance
      const bids: OrderBookEntry[] = (data.bids || [])
        .slice(0, 50) // Limit to first 50 entries for performance
        .map((bid: [string, string]) => {
          const price = parseFloat(bid[0]);
          const quantity = parseFloat(bid[1]);
          return {
            price,
            quantity,
            total: price * quantity
          };
        })
        .filter((bid: OrderBookEntry) => bid.price > 0 && bid.quantity > 0);

      // Process asks (sell orders) - limit processing for performance
      const asks: OrderBookEntry[] = (data.asks || [])
        .slice(0, 50) // Limit to first 50 entries for performance
        .map((ask: [string, string]) => {
          const price = parseFloat(ask[0]);
          const quantity = parseFloat(ask[1]);
          return {
            price,
            quantity,
            total: price * quantity
          };
        })
        .filter((ask: OrderBookEntry) => ask.price > 0 && ask.quantity > 0);

      // Sort bids (descending) and asks (ascending)
      bids.sort((a, b) => b.price - a.price);
      asks.sort((a, b) => a.price - b.price);

      // Calculate spread
      const spread = asks.length > 0 && bids.length > 0 ? asks[0].price - bids[0].price : 0;
      const midPrice = asks.length > 0 && bids.length > 0 ? (asks[0].price + bids[0].price) / 2 : 0;
      const spreadPercentage = midPrice > 0 ? (spread / midPrice) * 100 : 0;

      const orderBookData: OrderBookData = {
        symbol,
        lastUpdateId,
        bids: bids.slice(0, 20), // Reduced from 400 to 20 for performance
        asks: asks.slice(0, 20), // Reduced from 400 to 20 for performance
        spread,
        spreadPercentage,
        timestamp: Date.now()
      };

      // Update internal data
      this.orderBookData.set(symbol, orderBookData);

      // Create market depth data
      const maxVolume = Math.max(
        ...bids.map(bid => bid.quantity),
        ...asks.map(ask => ask.quantity)
      );

      const marketDepthData: MarketDepthData = {
        symbol,
        sells: asks.slice(0, 1), // Top 1 ask for visualization
        buys: bids.slice(0, 1),  // Top 1 bid for visualization
        maxVolume,
        timestamp: Date.now()
      };

      this.marketDepthData.set(symbol, marketDepthData);

      // Use requestAnimationFrame to defer listener notifications for better performance
      requestAnimationFrame(() => {
        this.notifyOrderBookListeners(symbol, orderBookData);
        this.notifyDepthListeners(symbol, marketDepthData);
      });

    } catch (error) {
      console.error(`Error processing order book data for ${symbol}:`, error);
    }
  }

  /**
   * Notify order book listeners
   */
  private notifyOrderBookListeners(symbol: string, data: OrderBookData) {
    this.listeners.forEach(callback => {
      try {
        callback(symbol, data);
      } catch (error) {
        console.error('Error in order book listener callback:', error);
      }
    });
  }

  /**
   * Notify market depth listeners
   */
  private notifyDepthListeners(symbol: string, data: MarketDepthData) {
    this.depthListeners.forEach(callback => {
      try {
        callback(symbol, data);
      } catch (error) {
        console.error('Error in market depth listener callback:', error);
      }
    });
  }

  /**
   * Handle WebSocket reconnection
   */
  private handleReconnect(symbol: string) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Max reconnection attempts reached for ${symbol}`);
      return;
    }

    this.reconnectAttempts++;
    // Capped: this used to double without limit, so a long-lived tab that had reconnected
    // a few times ended up waiting minutes before trying again.
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);

    // Tracked so gracefulDisconnect/cleanup can cancel a pending retry rather than
    // letting it reopen a socket for a symbol the user has already navigated away from.
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.isAuthenticated && this.subscribedSymbols.has(symbol)) {
        this.connectWebSocket(symbol);
      }
    }, this.reconnectDelay);
  }

  /**
   * Disconnect WebSocket (legacy method - use gracefulDisconnect for new code)
   */
  private disconnectWebSocket() {
    this.forceDisconnect();
  }

  /**
   * Get current order book data for a symbol
   */
  getOrderBookData(symbol: string): OrderBookData | undefined {
    return this.orderBookData.get(symbol);
  }

  /**
   * Get current market depth data for a symbol
   */
  getMarketDepthData(symbol: string): MarketDepthData | undefined {
    return this.marketDepthData.get(symbol);
  }

  /**
   * Cleanup service
   */
  cleanup() {
    this.gracefulDisconnect().then(() => {
      this.listeners.clear();
      this.depthListeners.clear();
      this.subscribedSymbols.clear();
      this.orderBookData.clear();
      this.marketDepthData.clear();
      this.currentSymbol = null;
    });
  }
}

// Export singleton instance
export const orderBookService = new OrderBookService();
export default orderBookService;
